import type { IconsType } from "shared/types/sked-ui";
import React, { useState } from "react";
import styled, { css } from "styled-components";
import { useSelector } from "react-redux";
import { Modal } from "@/components/modal/modal";
import { AccountsCount } from "api/accounts";
import { SocialAccountType } from "../../SocialAccountType";
import { NeedHelpButton } from "../need-help-button";
import { Icon } from "@dialogueconsulting/sked-ui";
import { selectAllAccounts } from "@/entities/accounts/table";
import { SOCIAL_SET_SLOTS, SocialSetSlotKey } from "utils/social-set";
import { formatAccountsLimit } from "pages/accounts/utils/format-accounts-limit";

interface SocialAccountConnectDialogProps {
  show: boolean;
  isLoading: boolean;
  counters: AccountsCount;
  isLocked?: boolean;
  /**
   * When set, the user is on the social-set-only entitlement (one account
   * per platform). The dialog disables individual platform cards whose slot
   * is already filled and shows tailored copy in the section header.
   * Presence of this prop is the signal — pass `undefined` to opt out.
   */
  socialSetMode?: {
    usedSlots: Set<SocialSetSlotKey>;
  };

  onHide(): void;

  onConnectAccount(type: SocialAccountType): void;
}

const PLATFORM_ICON_MAP = {
  instagram: "instagram",
  facebook: "facebook",
  youtube: "youtube",
  threads: "threads",
  tiktok: "tiktok",
  googlemybusiness: "googlemybusiness",
  linkedin: "linkedin",
  twitter: "twitter",
  pinterest: "pinterest",
  snapchat: "snapchat",
  other: "other",
} as const satisfies Record<string, IconsType>;

type PlatformIconKey = keyof typeof PLATFORM_ICON_MAP;

interface PlatformCardProps {
  title: string;
  subTitle: string;
  iconKey: PlatformIconKey;
  onClick: () => void;
  isTrending?: boolean;
  disabled?: boolean;
  /** Tooltip text shown when the card is disabled (e.g., slot already used). */
  disabledTooltip?: string;
}

/**
 * Returns true if the slots required for a given platform card are ALL
 * filled in social-set mode. Most platforms map to a single slot; LinkedIn
 * spans two (personal + company) and is only disabled when both are taken.
 */
const isPlatformSlotTaken = (
  iconKey: PlatformIconKey,
  usedSlots: Set<SocialSetSlotKey>
): boolean => {
  switch (iconKey) {
    case "instagram": {
      return usedSlots.has("IG");
    }
    case "facebook": {
      return usedSlots.has("FB");
    }
    case "tiktok": {
      return usedSlots.has("TT");
    }
    case "threads": {
      return usedSlots.has("TH");
    }
    case "youtube": {
      return usedSlots.has("YT");
    }
    case "googlemybusiness": {
      return usedSlots.has("GMB");
    }
    case "linkedin": {
      // LinkedIn has two slots (personal + company); only disable when BOTH
      // are taken. The downstream LinkedinConnectDialog lets the user choose.
      return usedSlots.has("LI:personal") && usedSlots.has("LI:company");
    }
    case "twitter": {
      return usedSlots.has("TW");
    }
    case "pinterest": {
      return usedSlots.has("PI");
    }
    default: {
      return false;
    }
  }
};

export function NewSocialAccountConnectDialog({
  show,
  onHide,
  isLoading,
  onConnectAccount,
  counters,
  isLocked = false,
  socialSetMode,
}: SocialAccountConnectDialogProps): React.ReactElement {
  const isLegacyPricing =
    counters.pricingVersion && !counters.pricingVersion.includes("2024");

  const usedCount = isLegacyPricing ? counters.igCount : counters.totalCount;
  const maxAllowedCount = isLegacyPricing
    ? counters.igAllowed
    : counters.maxAllowed;
  // SKE-3543 round 7: server returns MAX_SAFE_INTEGER (or near-max) as the
  // "Unlimited" sentinel for trial-floor + custom-tier users. Derive both
  // the boolean + the rendered value from the same helper so the two
  // never drift, and undefined/NaN are handled identically in both paths.
  const formattedMaxAllowed = formatAccountsLimit(maxAllowedCount);
  const isUnlimited = formattedMaxAllowed === "Unlimited";
  const hasYoutubeAccount = counters?.ytCount > 0;
  const hasTiktokAccount = counters?.ttCount > 0;

  const accountsCount = useSelector(selectAllAccounts).length;

  const inSocialSetMode = socialSetMode !== undefined;
  const usedSlots = socialSetMode?.usedSlots ?? new Set<SocialSetSlotKey>();
  const usedSocialSetCount = usedSlots.size;

  const socialPlatforms = [
    {
      title: "Instagram",
      subTitle: "Business profile",
      iconKey: "instagram",
      onClick: () => onConnectAccount(SocialAccountType.Instagram),
    },
    {
      title: "Facebook",
      subTitle: "Page",
      iconKey: "facebook",
      onClick: () => onConnectAccount(SocialAccountType.FacebookPage),
    },
    {
      title: "TikTok",
      subTitle: "Any account type",
      iconKey: "tiktok",
      isTrending: !hasTiktokAccount,
      onClick: () => onConnectAccount(SocialAccountType.TikTok),
    },
    {
      title: "Threads",
      subTitle: "Any account type",
      iconKey: "threads",
      onClick: () => onConnectAccount(SocialAccountType.ThreadsConnect),
    },
    {
      title: "Youtube",
      subTitle: "Any account type",
      iconKey: "youtube",
      isTrending: !hasYoutubeAccount,
      onClick: () => onConnectAccount(SocialAccountType.Youtube),
    },
    {
      title: "Google Business Profile",
      subTitle: "Business locations",
      iconKey: "googlemybusiness",
      onClick: () => onConnectAccount(SocialAccountType.GoogleMyBusiness),
    },
    {
      title: "LinkedIn",
      subTitle: "Any account type",
      iconKey: "linkedin",
      onClick: () => onConnectAccount(SocialAccountType.LinkedInCompany),
    },
    {
      title: "X",
      subTitle: "Any account type",
      iconKey: "twitter",
      onClick: () => onConnectAccount(SocialAccountType.Twitter),
    },
    {
      title: "Pinterest",
      subTitle: "Any account type",
      iconKey: "pinterest",
      onClick: () => onConnectAccount(SocialAccountType.Pinterest),
    },
  ] satisfies PlatformCardProps[];

  const notificationPlatforms = [
    {
      title: "Instagram",
      subTitle: "Personal profile",
      iconKey: "instagram",
      onClick: () => onConnectAccount(SocialAccountType.InstagramPersonal),
    },
    {
      title: "Snapchat",
      subTitle: "Any account type",
      iconKey: "snapchat",
      onClick: () => onConnectAccount(SocialAccountType.Snapchat),
    },
    {
      title: "Other",
      subTitle: "Any account type",
      iconKey: "other",
      onClick: () => onConnectAccount(SocialAccountType.Other),
    },
  ] satisfies PlatformCardProps[];

  const modalBehaviorProps = isLocked
    ? ({ backdrop: "static" as const, keyboard: false } as const)
    : undefined;
  return (
    <StyledBaseModal
      data-testid="newSocialAccountConnectionDialog"
      show={show}
      onHide={onHide}
      {...(modalBehaviorProps || {})}
    >
      <Modal.Header closeButton onHide={onHide}>
        <Modal.Title>
          {accountsCount > 0
            ? "Add more social accounts"
            : "Add social accounts"}
        </Modal.Title>
        <NeedHelpButton chatQuery="accounts" />
      </Modal.Header>
      <Content className="mt-4">
        <SectionWrap>
          <SectionHead>
            <SectionHeadTitle>Social media accounts</SectionHeadTitle>
            <SectionHeadContent>
              {inSocialSetMode ? (
                <>
                  Your plan includes one of each platform —{" "}
                  <span>{`${usedSocialSetCount}/${SOCIAL_SET_SLOTS.length}`}</span>{" "}
                  connected.{" "}
                  {usedSocialSetCount >= SOCIAL_SET_SLOTS.length ? (
                    <strong>
                      You&apos;ve connected one of every platform on your plan.
                      Manage your plan to switch tiers.
                    </strong>
                  ) : null}
                </>
              ) : isUnlimited ? (
                <>
                  Your current plan includes <span>unlimited</span> social media
                  accounts.
                </>
              ) : (
                <>
                  On your current plan, you can add a total of{" "}
                  <span>{`${usedCount}/${formattedMaxAllowed}`}</span> social
                  media accounts.{" "}
                  {typeof maxAllowedCount === "number" &&
                  usedCount >= maxAllowedCount ? (
                    <strong>
                      You need to upgrade your plan to add more accounts, or
                      remove an account to add more.
                    </strong>
                  ) : null}
                </>
              )}
            </SectionHeadContent>
          </SectionHead>
          <SectionContent>
            {socialPlatforms.map((platform) => {
              const slotTaken =
                inSocialSetMode &&
                isPlatformSlotTaken(platform.iconKey, usedSlots);
              // LinkedIn has two slots (personal + company); when both are
              // filled the card disables and we want to be specific about
              // why, since "already added" reads oddly when the user has
              // only added one of the two LinkedIn variants.
              const tooltip = slotTaken
                ? platform.iconKey === "linkedin"
                  ? "Both LinkedIn slots used (personal and company)."
                  : "Already added — your plan includes one of each platform."
                : undefined;
              return (
                <PlatformCard
                  key={platform.title}
                  {...platform}
                  disabled={isLoading || slotTaken}
                  disabledTooltip={tooltip}
                />
              );
            })}
          </SectionContent>
        </SectionWrap>

        <SectionWrap>
          <SectionHead>
            <SectionHeadTitle>
              Notification channels
              <a
                href="https://help.skedsocial.com/en/articles/8333393-how-post-notifications-work-in-sked"
                target="_blank"
                rel="noreferrer"
              >
                <Icon name="help-circle" color="#808080" size="sm" />
              </a>
            </SectionHeadTitle>
            <SectionHeadContent>
              Go wild—add as many notification channels as you want for push
              reminders to your phone.
            </SectionHeadContent>
          </SectionHead>
          <SectionContent>
            {notificationPlatforms.map((platform) => (
              <PlatformCard
                key={platform.title}
                {...platform}
                disabled={isLoading}
              />
            ))}
          </SectionContent>
        </SectionWrap>
      </Content>
    </StyledBaseModal>
  );
}

function PlatformCard(props: PlatformCardProps) {
  const [isHover, setIsHover] = useState(false);
  const isDisabled = props.disabled === true;
  return (
    <PlatformCardWrap
      type="button"
      onMouseEnter={() => !isDisabled && setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      isHover={isHover && !isDisabled}
      isDisabled={isDisabled}
      onClick={props.onClick}
      title={isDisabled ? props.disabledTooltip : undefined}
      disabled={isDisabled}
    >
      <CardLeft>
        <Icon name={PLATFORM_ICON_MAP[props.iconKey]} size="xl" />
      </CardLeft>
      <CardMid>
        <PlatformTitle>{props.title}</PlatformTitle>
        <PlatformSubTitle>{props.subTitle}</PlatformSubTitle>
      </CardMid>
      <CardRight>
        {/* Success green — keep in sync with SocialSetCheck in accounts-counter
            and --signal-positive in inbox-tokens. */}
        {isDisabled && <Icon name="check-circle" size="md" color="#16a34a" />}
        {!isDisabled && isHover && (
          <Icon name="plus" size="md" color="#4F31FF" />
        )}
        {!isDisabled && !isHover && props.isTrending && (
          <TrendMark>
            <Icon name="trend-up-01" /> <span>Trending</span>
          </TrendMark>
        )}
      </CardRight>
    </PlatformCardWrap>
  );
}

const SectionWrap = styled.div`
  margin-bottom: 16px;
`;
const SectionHead = styled.div`
  display: flex;
  padding: 8px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  background: var(--Primary-P50, #edeaff);
  border-radius: 8px;
`;
const SectionHeadTitle = styled.div`
  color: var(--Text-T01, #333);
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
  display: flex;
  align-items: center;

  a {
    margin-left: 5px;
  }
`;
const SectionHeadContent = styled.div`
  color: var(--Text-T01, #333);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */

  span {
    font-weight: bold;
  }
`;
const SectionContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;
const PlatformCardWrap = styled.button<{
  isHover: boolean;
  isDisabled?: boolean;
}>`
  flex-basis: calc(calc(100% - 8px) / 2);
  display: flex;
  width: calc(calc(100% - 8px) / 2);
  padding: 8px 16px;
  align-items: center;
  gap: 16px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid var(--Neutral-N100, #e6e6e6);
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.isDisabled ? 0.55 : 1)};
  text-align: left;
  appearance: none;

  &:disabled {
    cursor: not-allowed;
  }

  ${(props) =>
    props.isHover &&
    !props.isDisabled &&
    css`
      background: var(--Primary-P25, #fcfaff);
      box-shadow:
        0px 1px 3px 0px rgba(16, 24, 40, 0.1),
        0px 1px 2px 0px rgba(16, 24, 40, 0.06);
    `}
`;
const CardLeft = styled.div`
  flex-basis: 25px;
`;
const CardMid = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
const CardRight = styled.div`
  flex-basis: 75px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const PlatformTitle = styled.div`
  color: var(--Text-T01, #333);
  text-align: center;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
  display: flex;
  align-items: center;
`;
const PlatformSubTitle = styled.div`
  color: var(--Text-T01, #333);
  text-align: center;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
`;
const TrendMark = styled.div`
  display: flex;
  display: inline-flex;
  padding: 2px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 20px;
  background: var(--Yellow-Y400, #fbbf24);
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    color: var(--Text-T02, #454545);

    /* Text xxs/Semibold */
    font-family: Inter;
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 14px; /* 140% */
  }
`;

const Content = styled(Modal.Body)`
  h3 {
    margin: 1px 0 11px 0;
    font-weight: 500;
    font-size: 14px;
    line-height: 125.5%;
    letter-spacing: -0.04em;
    text-align: center;
  }
`;

const StyledBaseModal = styled(Modal)`
  max-width: 1000px;
  width: 100%;
  h2 {
    color: var(--Primary-P700, #0e1680);
    font-family: "Gotham SSm A", "Gotham SSm B", Helvetica, Arial, sans-serif;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 32px; /* 133.333% */
  }
`;
